import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getCorrections,
  undoCorrection,
} from "../../../services/readingOrdersApi";
import { useToast } from "@chakra-ui/react";

/**
 * Get corrections submitted to a reading order + functions
 */
export default function useCorrections(readingOrderId) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isSuccess, isLoading } = useQuery(
    ["readingOrderCorrections", readingOrderId],
    () => {
      return getCorrections(readingOrderId);
    }
  );

  const undoCorrectionMutation = useMutation(
    ({ correctionId }) => {
      return undoCorrection(readingOrderId, correctionId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "readingOrderCorrections",
          readingOrderId,
        ]);
        toast({
          title: "Suggestion undone",
          description: "Your suggestion has been removed.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const mergeCorrections = (comics = [], corrections = []) => {
    // Separate add suggestions
    const correctedComics = corrections
      .map((correction) => {
        return correction.action === 1
          ? {
              ...correction.newComic,
              order: correction.order,
              story: correction.story,
              action: correction.action,
              correctionId: correction.id,
            }
          : null;
      })
      .filter((x) => x !== null);

    // Add edit and delete suggestions to the comics with add suggestions
    const comicsWithCorrections = [...comics, ...correctedComics].map(
      (comic) => {
        const correction = corrections.find(
          (correction) => correction.order === comic.order
        );
        if (correction && correction.action === 2) {
          return {
            ...comic,
            ...correction.newComic,
            order: comic.order,
            story: correction.newStory,
            action: correction.action,
            correctionId: correction.id,
          };
        }
        if (correction && correction.action === 3) {
          return {
            ...comic,
            action: correction.action,
            correctionId: correction.id,
          };
        }
        return comic;
      }
    );

    return comicsWithCorrections.sort((a, b) => a.order - b.order);
  };

  const onClickUndoCorrection = (correctionId) => {
    // console.log("undoing...", correctionId);
    undoCorrectionMutation.mutate({ correctionId });
  };

  return {
    corrections: data?.data || [],
    correctionsLoading: isLoading,
    correctionsLoaded: isSuccess,
    mergeCorrections,
    undoCorrection: onClickUndoCorrection,
  };
}
